import type {
  PresentationThemeApi,
  PresentationThemeCatalogVm,
  PresentationThemeListApi,
  PresentationThemeOption,
  PresentationThemeTone,
} from "./presentationThemes.types";

/** Palette tokens that hold a theme's slide background, in the order they are tried. */
const BACKGROUND_TOKENS = ["slideBackground", "background"] as const;

/**
 * A theme's tone from the catalog: its own `tone` when the file states one, else whether the
 * palette's slide background is dark (relative luminance under 0.5), else light.
 */
export const presentationThemeTone = (
  theme: Pick<PresentationThemeApi, "palette" | "tone">,
): PresentationThemeTone => {
  if (theme.tone === "light" || theme.tone === "dark") return theme.tone;
  const background = BACKGROUND_TOKENS.map((token) => theme.palette?.[token]).find(
    (value): value is string => typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value),
  );
  if (!background) return "light";
  const n = parseInt(background.slice(1), 16);
  const luminance = (0.2126 * ((n >> 16) & 255) + 0.7152 * ((n >> 8) & 255) + 0.0722 * (n & 255)) / 255;
  return luminance < 0.5 ? "dark" : "light";
};

export const mapPresentationThemeOption = (
  theme: PresentationThemeApi,
): PresentationThemeOption => ({
  key: theme.key,
  label: theme.label || theme.key,
  swatch: theme.palette?.purplePrimary,
  tone: presentationThemeTone(theme),
  palette: theme.palette ?? {},
});

export const mapPresentationThemeCatalog = (
  response: PresentationThemeListApi,
): PresentationThemeCatalogVm => ({
  defaultThemeKey: response.default_theme,
  options: (response.themes ?? []).map(mapPresentationThemeOption),
});
